"use client";
import Image from "next/image";
import { useEffect, useState } from "react";
import { getStrapiMedia } from "../utils/api-helpers";

interface ImageAsset {
  id: number;
  attributes: {
    alternativeText: string | null;
    caption: string | null;
    url: string;
  };
}

interface SlidShowProps {
  files: {
    data: ImageAsset[];
  };
}

export default function Slideshow({ data }: { data: SlidShowProps }) {
  const slides = data?.files?.data ?? [];
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (slides.length <= 1 || isHovered) {
      return;
    }

    const timer = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => window.clearInterval(timer);
  }, [slides.length, isHovered]);

  useEffect(() => {
    if (activeIndex >= slides.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, slides.length]);

  const goToSlide = (direction: "next" | "prev") => {
    if (slides.length <= 1) {
      return;
    }

    setActiveIndex((prev) => {
      if (direction === "next") {
        return (prev + 1) % slides.length;
      }

      return (prev - 1 + slides.length) % slides.length;
    });
  };

  if (!slides.length) {
    return null;
  }

  return (
    <div
      className="relative h-96 w-full overflow-hidden rounded-lg"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {slides.map((slide, index) => {
        const imageUrl = getStrapiMedia(slide.attributes.url);

        if (!imageUrl) {
          return null;
        }

        const isActive = index === activeIndex;

        return (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
              isActive ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden={isActive ? "false" : "true"}
          >
            <Image
              className="h-96 w-full object-cover"
              height={400}
              width={600}
              alt={
                slide.attributes.alternativeText ||
                slide.attributes.caption ||
                "Carousel image"
              }
              src={imageUrl}
              priority={index === 0}
            />
          </div>
        );
      })}

      {slides.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous slide"
            className="absolute left-4 top-1/2 z-10 flex h-10 w-20 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-black text-sm font-semibold shadow-md transition hover:bg-white"
            onClick={() => goToSlide("prev")}
          >
            Prev
          </button>
          <button
            type="button"
            aria-label="Next slide"
            className="absolute right-4 top-1/2 z-10 flex h-10 w-20 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-black text-sm font-semibold shadow-md transition hover:bg-white"
            onClick={() => goToSlide("next")}
          >
            Next
          </button>

          <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-2">
            {slides.map((slide, index) => (
              <button
                key={slide.id}
                type="button"
                aria-label={`Go to slide ${index + 1}`}
                className={`h-2 w-2 rounded-full transition ${
                  index === activeIndex ? "bg-white" : "bg-white/50"
                }`}
                onClick={() => setActiveIndex(index)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
